/* eslint-disable react/prop-types */
import { Link, useNavigate } from 'react-router-dom';
import { Store, ShoppingBag, Package, User, ExternalLink, Calendar } from 'lucide-react';
import { useOrders } from '../../../contexts/OrderContext';
import { useUser } from '../../../contexts/UserContext';
import { Avatar, AvatarFallback, AvatarImage } from '@/components/ui/avatar';

const navItems = [
  { to: '/store', icon: Store, label: 'Store', pageName: 'dashboard' },
  { to: '/', icon: ShoppingBag, label: 'Orders', pageName: 'orders' },
  { to: '/events', icon: Calendar, label: 'Dashboard', pageName: 'events' },
  { to: '/product/pricing', icon: Package, label: 'Products', pageName: 'pricing' },
];

const flagStyles = `
  @keyframes tricolorWave {
    0% { 
      background-position: 0% 0%;
      transform: skewX(0deg) scaleX(1);
    }
    25% { 
      background-position: 10% 0%;
      transform: skewX(2deg) scaleX(1.02);
    }
    50% { 
      background-position: 20% 0%;
      transform: skewX(0deg) scaleX(1);
    }
    75% { 
      background-position: 10% 0%;
      transform: skewX(-2deg) scaleX(0.98);
    }
    100% { 
      background-position: 0% 0%;
      transform: skewX(0deg) scaleX(1);
    }
  }
  
  @keyframes tricolorShift {
    0%, 100% { 
      background-size: 100% 100%;
    }
    50% { 
      background-size: 120% 100%;
    }
  }
  
.tricolor-text {
  background-attachment: fixed;
  animation: tricolorWave 2.5s ease-in-out infinite, tricolorShift 3s ease-in-out infinite;
  font-weight: 700;
  letter-spacing: 0.5px;
  display: inline-block;
  transform-origin: left center;
}

  .profile-icon-tricolor {
  background: linear-gradient(
    45deg, 
    #138808 0%,   /* Green */
    #138808 50%,   /* Green */
    #FF9933 50%,   /* Orange */
    #FF9933 100%   /* Orange */
  );
  background-clip: text;
  -webkit-background-clip: text;
  -webkit-text-fill-color: transparent;
  animation: tricolorWave 2s ease-in-out infinite;
  font-weight: 600;
}

`;

export default function Header({ page = 'dashboard' }) {
  const { orderCount } = useOrders();
  const { user } = useUser();
  const navigate = useNavigate();

  // Check if user is an event vendor
  const isEventVendor =
    user?.attributeValues?.find((attr) => attr.name === 'isEventVendor')?.value === 'true';

  // Filter nav items based on user type
  const filteredNavItems = navItems.filter((item) => {
    // Show events link only if user is an event vendor
    if (item.pageName === 'events') {
      return isEventVendor;
    }
    return true;
  });

  return (
    <>
      <style>{flagStyles}</style>
      <header className="sticky top-0 z-30 bg-white/95 backdrop-blur-lg border-b border-slate-200">
        <div className=" mx-auto flex items-center justify-between h-16 px-3 md:px-6">
          {/* Left Side: Branding */}
          <div
            onClick={() => {
              navigate(`/vendor/profile`, {
                state: {
                  from: '/store',
                  vendorData: user,
                },
              });
            }}
            className="flex items-center gap-2"
          >
            <Store className="w-4 h-4 text-orange-600" />
            <div className="flex flex-col">
              <span className="text-base line-clamp-1 font-semibold text-slate-800 leading-tight">
                {user?.shopName}
              </span>
              <div className="flex items-center gap-2">
                {user?.name && user?.name !== user?.shopName && (
                  <span className="text-xs text-slate-600 leading-tight">{user.name}</span>
                )}
                <span className="text-xs flex items-center gap-1 font-medium cursor-pointer">
                  <span className="tricolor-text text-green-700">Profile</span>
                  <ExternalLink className="size-3 animate-bounce text-green-700" />
                </span>
              </div>
            </div>
          </div>

          {/* Right Side: Actions & Profile */}
          <div className="flex items-center gap-3">
            {/* Middle: Desktop Navigation (hidden on mobile) */}
            <nav className="hidden md:flex items-center gap-2">
              {filteredNavItems.map((item) => {
                const isActive = page === item.pageName;
                return (
                  <Link
                    key={item.label}
                    to={item.to}
                    className={`flex relative items-center gap-2 px-4 py-2 text-sm font-semibold rounded-full transition-colors ${
                      isActive
                        ? 'bg-blue-50 text-blue-700'
                        : 'text-slate-600 hover:bg-slate-100 hover:text-slate-800'
                    }`}
                  >
                    <item.icon
                      className={`h-4 w-4 ${isActive ? 'text-blue-600' : 'text-slate-500'}`}
                    />
                    <span>{item.label}</span>
                  </Link>
                );
              })}
            </nav>
            <button
              onClick={() => navigate('/profile')}
              className="relative flex items-center justify-center rounded-full"
            >
              {user?.profileImage ? (
                <Avatar className="size-10">
                  <AvatarImage src={user.profileImage} alt={user.name || 'User Profile'} />
                  <AvatarFallback>
                    {user.name ? user.name.charAt(0).toUpperCase() : 'U'}
                  </AvatarFallback>
                </Avatar>
              ) : (
                <div className="flex items-center justify-center bg-slate-200 rounded-full size-10">
                  <User className="size-6 text-slate-600" />
                </div>
              )}
            </button>
          </div>
        </div>
      </header>
    </>
  );
}
